import { Box, Button, Grid, Link, Tooltip, Typography } from "@mui/material"
import { useTheme } from "@mui/material/styles"
import { t } from "i18next"
import { useState } from "react"
import { License } from "../../constants/license.enum"
import { dateToString } from "../../helpers/date.helpers"
import { getUserLicenseState, UserLicenseType } from "../../helpers/license.helpers"
import { splitIntoSentences } from "../../helpers/string.helpers"
import { useAppSelector } from "../../hooks/useRedux"
import UpgradeDialog from "./UpgradeDialog"

export default function UserLicense() {
    const theme = useTheme()
    const user = useAppSelector((state) => state.user.value)
    const licenseObj = useAppSelector((state) => state.user.value.license)
    const [openUpgradeDialog, setOpenUpgradeDialog] = useState<boolean>(false)

    const userLicenseState = getUserLicenseState(
        licenseObj?.license ?? License.Free,
        licenseObj?.started,
        licenseObj?.expired,
        user.createdAt,
        licenseObj?.isRevoked
    )

    return (
        <>
            <Box sx={{ backgroundColor: theme.palette.background.paper, p: 4 }}>
                <Grid container direction="column" spacing={1}>
                    <Grid item>
                        <Typography variant="h6" mb={1} sx={{ fontWeight: "bold" }}>
                            {t(`user:userLicense.title.${userLicenseState.type}`)}
                        </Typography>
                    </Grid>
                    <Grid item>
                        <Typography variant="body1" mb={2}>
                            {t(`user:userLicense.text.${userLicenseState.type}`)}
                        </Typography>
                        {splitIntoSentences(
                            t(`user:userLicense.text&Date.${userLicenseState.type}`, {
                                expired:
                                    userLicenseState.expired &&
                                    dateToString(userLicenseState.expired),
                                started:
                                    userLicenseState.started &&
                                    dateToString(userLicenseState.started),
                                next:
                                    userLicenseState.nextBillingDate &&
                                    dateToString(userLicenseState.nextBillingDate),
                                createdAt:
                                    userLicenseState.createdAt &&
                                    dateToString(userLicenseState.createdAt),
                            })
                        ).map((sentence, index) => (
                            <Typography key={index} variant="body1">
                                {sentence}
                            </Typography>
                        ))}
                    </Grid>
                </Grid>
                {[
                    UserLicenseType.Free,
                    UserLicenseType.Unlimited,
                    UserLicenseType.UnlimitedPlanDowngraded,
                ].includes(userLicenseState.type as UserLicenseType) ? (
                    <Grid container mt={3}>
                        <>
                            <Tooltip placement={"right"} title={t("user:button:upgradeTooltip")}>
                                <span>
                                    <Button
                                        disabled={false}
                                        onClick={() => setOpenUpgradeDialog(true)}
                                    >
                                        {t(`user:userLicense.button.${userLicenseState.type}`)}
                                    </Button>
                                </span>
                            </Tooltip>
                        </>
                    </Grid>
                ) : (
                    <Grid container mt={2}>
                        <Typography variant="body1">
                            {t(
                                `user:userLicense.contactText${
                                    userLicenseState.type === undefined ? "Undefined" : ""
                                }`
                            )}{" "}
                            <Link href={`mailto:${t("common:supportEmail")}`} underline="hover">
                                {t("common:supportEmail")}
                            </Link>
                        </Typography>
                    </Grid>
                )}
            </Box>
            {openUpgradeDialog && (
                <UpgradeDialog
                    open={openUpgradeDialog}
                    licenseType={userLicenseState.type}
                    onClose={() => setOpenUpgradeDialog(false)}
                />
            )}
        </>
    )
}
